import {
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';

/**
 * Composable for list keyboard navigation and auto-loading functionality.
 *
 * @param {Object} options - Configuration options
 * @param {import('vue').Ref|import('vue').ComputedRef} options.items - List items (ref or computed)
 * @param {string} options.containerSelector - CSS selector for the list container (e.g. '.my-list .list-page-table .list-item')
 * @param {import('vue').Ref} options.dataLoadParameters - Data load parameters from useInfiniteFetch
 * @param {Function} options.showMore - Function to load more items
 * @param {Function} options.onItemSelected - Callback when an item is selected (item, index) => void
 * @param {import('vue').Ref|import('vue').ComputedRef} [options.isEnabled] - Optional ref to enable/disable
 * navigation (default: always enabled)
 * @param {number} [options.autoLoadThreshold=6] - Number of items from end to trigger auto-load
 * @returns {Object} Navigation state and methods
 */
export const useListNavigation = ({
  items,
  containerSelector,
  dataLoadParameters,
  showMore,
  onItemSelected,
  isEnabled = ref(true),
  autoLoadThreshold = 6,
}) => {
  const selectedItemIndex = ref(0);
  const isAutoLoadingMore = ref(false);
  const lastAutoLoadTriggeredAtLength = ref(null);

  /**
   * Scroll the selected row into view.
   * @param {number} index - Index of the row to scroll into view
   */
  const scrollRowIntoView = async index => {
    await nextTick();
    const rows = document.querySelectorAll(containerSelector);
    const rowElement = rows[index];

    if (rowElement?.scrollIntoView) {
      rowElement.scrollIntoView({ block: 'nearest' });
    }
  };

  /**
   * Check if we need to load more items and trigger loading if necessary.
   */
  const checkAndLoadMore = async () => {
    const isNearEnd = selectedItemIndex.value >= items.value.length - autoLoadThreshold;
    const hasItems = items.value.length > 0;

    if (!isNearEnd || !hasItems) {
      return;
    }

    // Prevent multiple concurrent or repeated triggers.
    if (isAutoLoadingMore.value ||
      dataLoadParameters.value?.isFetching?.value ||
      dataLoadParameters.value?.isAllLoaded?.value) {
      return;
    }

    const currentLength = items.value.length;

    if (lastAutoLoadTriggeredAtLength.value === currentLength) {
      return;
    }

    lastAutoLoadTriggeredAtLength.value = currentLength;
    isAutoLoadingMore.value = true;

    try {
      await showMore();
    } finally {
      isAutoLoadingMore.value = false;
    }
  };

  // Watch selectedItemIndex to automatically load more when reaching the last items.
  watch(selectedItemIndex, () => {
    checkAndLoadMore();
  });

  /**
   * Handle keyboard arrow navigation between items.
   * @param {KeyboardEvent} event - Keyboard event
   */
  const handleKeyDown = async event => {
    // Check if navigation is enabled.
    if (!isEnabled.value) {
      return;
    }

    if (!items.value.length) {
      return;
    }

    const isArrowKey = event.key === 'ArrowUp' || event.key === 'ArrowDown';

    if (!isArrowKey) {
      return;
    }

    event.preventDefault();

    const direction = event.key === 'ArrowUp' ? -1 : 1;
    const newIndex = selectedItemIndex.value + direction;

    if (newIndex < 0 || newIndex >= items.value.length) {
      return;
    }

    const newItem = items.value[newIndex];
    selectItem(newItem, newIndex);
    await scrollRowIntoView(newIndex);
  };

  /**
   * Select an item at a given index.
   * @param {Object} item - The item to select
   * @param {number} index - The index of the item
   */
  const selectItem = (item, index) => {
    selectedItemIndex.value = index;
    onItemSelected(item, index);
  };

  /**
   * Select the first item in the list.
   */
  const selectFirstItem = () => {
    const firstItem = items.value.length > 0 ? items.value[0] : {};
    selectItem(firstItem, 0);
  };

  /**
   * Reset auto-load tracking (useful when refetching data).
   */
  const resetAutoLoadTracking = () => {
    lastAutoLoadTriggeredAtLength.value = null;
  };

  // Attach global keydown listener on mount.
  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown);
  });

  // Remove keydown listener on unmount.
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });

  return {
    selectedItemIndex,
    isAutoLoadingMore,
    selectItem,
    selectFirstItem,
    scrollRowIntoView,
    resetAutoLoadTracking,
  };
};

export default useListNavigation;
